import './LoginForm.scss';
import { Form } from '@components/Component/Container/Form/Form.js';
import { DataRecord } from '@components/DataRecord/DataRecord.js';
import { getID, oAuthRedirect } from '@components/Utility/index.js';
import DIContainer, { getInstance } from '@components/DependencyInjectionContainer.js';
import { ENV } from '@components/Environment.js';
import { DateTimeManager } from "@components/DateTimeManager/DateTimeManager.js";
/**
 * `LoginForm` class that extends `Form`.
 * This class represents a login page with input fields for userid and password and a submit button.
 */
export class LoginForm extends Form {
    get oAuthConfig() {
        return ENV('oAuthConfig') || [];
    }
    get IconManager() {
        if (!this.dependencies.IconManager) {
            // we didnt receive a IconManager dependency injection, so just use the global singleton
            this.dependencies.IconManager = getInstance('IconManager');
        }
        return this.dependencies.IconManager;
    }
    constructor(id, rootElement, dependencies = {}) {
        super(id, rootElement, dependencies);
        this.componentName = 'LoginForm';
        this.loadExternalData = false;
    }
    initializeRecords() {
        this.record = new DataRecord(getID('LoginForm'));
        this.record.get$('otpdestination')
            .subscribe((val) => {
            const btn = this.componentGet('requestotp');
            if (btn) {
                btn.disabled = !val;
            }
        });
        this.record.get$('authcode')
            .subscribe((val) => {
            const btn = this.componentGet('submitauth');
            if (btn) {
                btn.disabled = !(val && val.length === 6);
            }
        });
    }
    initializeComponents() {
        const userid = DIContainer.new('Input', 'userid');
        userid.propertiesLoad({
            captionKey: 'system_login_userid',
            showLabel: true,
            labelPosition: 'top',
            largeField: true
        });
        const password = DIContainer.new('InputPassword', 'password');
        password.propertiesLoad({
            captionKey: 'system_login_password',
            showLabel: true,
            labelPosition: 'top',
            largeField: true,
            maxLength: 200
        });
        const submit = DIContainer.new('Button', 'submit');
        submit.propertiesLoad({
            captionKey: 'system_login_login',
            buttonSize: 'large'
        });
        const passwordCaption = this.getCaption('system_login_password_placeholder');
        const usernameCaption = this.getCaption('system_login_userid_placeholder');
        submit.before('click', () => {
            if (!this.record.get('userid')) {
                this.alert({ caption: this.getCaption('system_requiredfield_caption', { field: usernameCaption }), modalStyle: 'error' });
                return false;
            }
            if (!this.record.get('password')) {
                this.alert({ caption: this.getCaption('system_requiredfield_caption', { field: passwordCaption }), modalStyle: 'error' });
                return false;
            }
            return true;
        });
        submit.on('click', () => {
            this.login();
        });
        const forgotpassword = DIContainer.new('TextBase', 'forgotpassword');
        forgotpassword.propertiesLoad({
            captionKey: 'system_login_forgotpassword',
            textElementType: 'a'
            //,zoomTemplate: '/form/forgotpassword?userid={{userid}}' //todo this prevents the link from working unless the userid is filled in so we need specific logic to handle that in TextBase if we want this to work
            ,
            zoomTemplate: '/form/forgotpassword'
        });
        const otpdestination = DIContainer.new('Radio', 'otpdestination');
        otpdestination.propertiesLoad({
            captionKey: 'system_login_login'
        });
        const requestotp = DIContainer.new('Button', 'requestotp');
        requestotp.propertiesLoad({
            captionKey: 'system_login_auth_otpselect',
            buttonSize: 'large',
            disabled: true
        });
        requestotp.before('click', () => {
            if (!this.record.get('otpdestination')) {
                this.alert({ caption: this.getCaption('system_login_auth_otpdestinationrequired', { field: usernameCaption }), modalStyle: 'warning' });
                return false;
            }
            return true;
        });
        requestotp.on('click', () => {
            this.requestOTP();
        });
        const returntologin = DIContainer.new('TextBase', 'returntologin');
        returntologin.propertiesLoad({
            captionKey: 'system_login_auth_returntologin',
            textElementType: 'a',
            zoomTemplate: '#'
        });
        returntologin.on('click', () => this.changeContainer());
        const returntologin2 = DIContainer.new('TextBase', 'returntologin2');
        returntologin2.propertiesLoad({
            captionKey: 'system_login_auth_returntologin',
            textElementType: 'a',
            zoomTemplate: '#'
        });
        returntologin2.on('click', () => this.changeContainer());
        //const authcode = DIContainer.new<InputAuthCode>('InputAuthCode', 'authcode');
        const authcode = DIContainer.new('InputAuthCode', 'authcode');
        authcode.propertiesLoad({
            captionKey: 'system_login_auth_code',
            showLabel: false,
            labelPosition: 'top',
            largeField: true,
            inputMode: 'numeric',
            maxLength: 6
        });
        const submitauth = DIContainer.new('Button', 'submitauth');
        submitauth.propertiesLoad({
            captionKey: 'system_login_auth_submitauth',
            buttonSize: 'large',
            disabled: true
        });
        submitauth.before('click', () => {
            if (!this.record.get('authcode')) {
                this.alert({ caption: this.getCaption('system_login_auth_authcoderequired', { field: usernameCaption }), modalStyle: 'warning' });
                return false;
            }
            return true;
        });
        submitauth.on('click', () => {
            this.validateOTP();
        });
        const trustedDevice = DIContainer.new('InputCheckbox', 'trusted_device');
        trustedDevice.propertiesLoad({
            captionKey: 'system_login_auth_trusteddevice',
            checkedValue: 'Y',
            uncheckedValue: 'N'
        });
        this.componentsAdd([
            userid,
            password,
            submit,
            forgotpassword,
            otpdestination,
            requestotp,
            returntologin,
            returntologin2,
            authcode,
            submitauth,
            trustedDevice
        ]);
        if (this.oAuthConfig.length) {
            this.oAuthConfig.forEach((config) => {
                const button = DIContainer.new('Button', config.webmailConfigNo);
                button.on('click', () => {
                    this.oAuthButtonClick(config.webmailConfigNo);
                });
                button.propertiesLoad({
                    caption: config.oAuthCaption,
                    buttonSize: 'large',
                    styleModifier: 'tertiary'
                });
                this.componentAdd(button);
            });
        }
    }
    oAuthButtonClick(webmailconfigno) {
        oAuthRedirect({ webmailconfigno });
    }
    changeContainer(container = 'login') {
        this.record.set('password', '');
        const authcode = this.componentGet('authcode');
        if (authcode) {
            authcode.clearInput();
        }
        const codeContainer = this.rootElement.querySelector('.code-form-container');
        const authContainer = this.rootElement.querySelector('.auth-form-container');
        const loginContainer = this.rootElement.querySelector('.login-form-container');
        //const formContainers = this.rootElement.querySelectorAll('[class*=form-container]');
        codeContainer.classList.toggle('active', container === 'code');
        authContainer.classList.toggle('active', container === 'auth');
        authContainer.classList.toggle('left', container === 'code');
        loginContainer.classList.toggle('active', container === 'login');
        if (container === 'code') {
            this.authCodeContainerSetup();
        }
    }
    /**
     * Executes login request to server.
     * If successful, the JWT cookie should be created so we do a window.location.reload which will now detect our JWT and open the application instead.
     */
    login() {
        this.Request.POST({
            endpoint: 'authenticate',
            loadingMessage: this.getCaption('system_loading_caption'),
            requestData: {
                userid: this.record.get('userid'),
                password: this.record.get('password')
            }
        }).subscribe((response) => {
            const data = response.payload?.result[0];
            if (data.tfa_required === 'Y') {
                this.setupOtpDestinations(data);
                this.changeContainer('auth');
            }
            else {
                this.finalizeLogin();
            }
        });
    }
    requestOTP() {
        this.Request.POST({
            endpoint: 'authenticate',
            action: 'requestotp',
            initiator: this.componentGet('requestotp'),
            requestData: {
                userid: this.record.get('auth_userid'),
                sessionid: this.record.get('sessionid'),
                format: this.record.get('otpdestination')
            }
        })
            .subscribe(() => {
            this.changeContainer('code');
        });
    }
    validateOTP() {
        this.Request.POST({
            endpoint: 'authenticate',
            action: 'validateotp',
            initiator: this.componentGet('submitauth'),
            requestData: {
                userid: this.record.get('auth_userid'),
                authcode: this.record.get('authcode'),
                sessionid: this.record.get('sessionid'),
                format: this.record.get('otpdestination'),
                user_name: this.record.get('user_name') // todo see above where this record value is being set
                ,
                trusted_device: this.record.get('trusted_device') || 'N'
            }
        })
            .subscribe(() => {
            this.finalizeLogin();
        });
    }
    finalizeLogin() {
        window.location.reload();
    }
    setupOtpDestinations(data) {
        const otpdestination = this.componentGet('otpdestination');
        const optionData = [];
        if (data.tfa_email) {
            optionData.push({ value: 'email', captionKey: 'system_login_auth_emailotp', captionKeyParams: { email: data.tfa_email } });
            this.record.set('tfa_email', data.tfa_email);
        }
        if (data.tfa_pushover === 'Y') {
            optionData.push({ value: 'pushover', captionKey: 'system_login_auth_pushover' });
        }
        if (data.tfa_totp === 'Y') {
            optionData.push({ value: 'totp', captionKey: 'system_login_auth_totp' });
        }
        otpdestination?.propertySet('optionData', optionData);
        if (data.sessionid) {
            this.record.set('sessionid', data.sessionid);
        }
        if (data.user_id) {
            this.record.set('auth_userid', data.user_id); // store their actual userid as they may have logged in with networklogin
        }
        if (data.user_name) {
            this.record.set('user_name', data.user_name); // store their actual userid as they may have logged in with networklogin
        }
    }
    // protected forgotPassword() {
    // 	this.navigate('forgotpassword', {
    // 		userid: this.record!.get('userid')
    // 	});
    // }
    authCodeContainerSetup() {
        const authcode = this.componentGet('authcode');
        if (authcode) {
            setTimeout(() => authcode.focus(), 500); // need to timeout or else it wrecks the flow of the css transitions
        }
        const container = this.rootElement.querySelector('.code-form-container');
        if (container) {
            let captionKey = 'system_login_auth_codesubheading'; //this is a failsafe
            let captionParam = {};
            let icon = 'password_2'; // failsafe
            switch (this.record.get('otpdestination')) {
                case 'totp':
                    captionKey = 'system_login_auth_codesent_totp';
                    icon = 'vpn_key';
                    break;
                case 'pushover':
                    captionKey = 'system_login_auth_codesent_pushover';
                    icon = 'phone_android';
                    break;
                case 'email':
                    captionKey = 'system_login_auth_codesent_email';
                    captionParam = { email: this.record.get('tfa_email') };
                    icon = 'mail';
                    break;
            }
            const subHeading = container.querySelector('.sub-heading');
            const iconContainer = container.querySelector('.tfa-icon');
            if (subHeading) {
                subHeading.innerHTML = this.CaptionManager.getCaption(captionKey, captionParam);
            }
            if (iconContainer) {
                iconContainer.innerHTML = this.IconManager.getIconString(icon);
            }
        }
    }
    mainHeadingCaptionGet(subHeading = false) {
        let caption;
        if (subHeading) {
            caption = this.CaptionManager.getCaption('system_login_subheading');
        }
        else {
            caption = this.CaptionManager.getCaption('system_login_heading');
        }
        return caption;
    }
    template() {
        let oAuth = '';
        const clientVersion = ENV('clientVersion');
        const dbVersion = ENV('dbVersion');
        const year = DateTimeManager.now().year;
        if (this.oAuthConfig.length) {
            oAuth += '<div class="or-separator">OR</div>';
            this.oAuthConfig.forEach((config) => {
                oAuth += `<div class="form-group"><span data-componentid="${config.webmailConfigNo}" class="oauth ${config.oAuthProvider.toLowerCase()}"></span></div>`;
            });
        }
        return `<div class="form-cover"></div>
<div class="form-wrapper">
	<div class="login-form-container active">
		<header>
			<span class="branding-logo">
			
			</span>
			<span class="heading">${this.mainHeadingCaptionGet()}</span>
			<span class="sub-heading">${this.mainHeadingCaptionGet(true)}</span>
		</header>
		<form class="thin">
				<div class="form-group">
					<span data-componentid="userid"></span>
				</div>
				<div class="form-group">
					<span data-componentid="password"></span>
				</div>
				<div class="form-group">
					<span data-componentid="forgotpassword"></span>
				</div>
				<div class="form-group">
					<span data-componentid="submit"></span>
				</div>
				${oAuth}
		</form>
	</div>
	<div class="auth-form-container">
		<header>
			<span class="tfa-icon">
				${this.IconManager.getIconString('lock')}
			</span>
			<span class="heading">${this.CaptionManager.getCaption('system_login_auth_heading')}</span>
			<span class="sub-heading">${this.CaptionManager.getCaption('system_login_auth_subheading')}</span>
		</header>
		<form class="thin">
				<div class="form-group">
					<span data-componentid="otpdestination"></span>
				</div>
				<div class="form-group">
					<span data-componentid="requestotp"></span>
				</div>
				<div class="form-group">
					<div class="return-container">
						<span data-componentid="returntologin"></span>
					</div>
				</div>
		</form>
	</div>
	<div class="code-form-container">
		<header>
			<span class="tfa-icon">
				${this.IconManager.getIconString('password')}
			</span>
			<span class="heading">${this.CaptionManager.getCaption('system_login_auth_codeheading')}</span>
			<span class="sub-heading"></span>
		</header>
		<form class="wide">
				<div class="form-group">
					<span data-componentid="authcode"></span>
				</div>
				<div class="form-group trusted-device-container">
					<span data-componentid="trusted_device"></span>
				</div>
				<div class="form-group">
					<span data-componentid="submitauth"></span>
				</div>
				<div class="form-group">
					<div class="return-container">
						<span data-componentid="returntologin2"></span>
					</div>
				</div>
		</form>
	</div>
	<footer>
		<span class="copyright">© ${year} SOLUTIONS360 Inc.</span>
		<span class="version">Version: ${clientVersion} / Database: ${dbVersion}</span>
	</footer>
</div>`;
    }
}
LoginForm.VERSION = '$Revision: 127735 $';
